/**
 * pageDocument.ts — Montagem do documento HTML de páginas públicas (landing)
 *
 * FONTE ÚNICA da montagem do artefato publicado. É usada tanto pela publicação
 * (`studio-publish-page`) quanto pela pré-visualização temporária
 * (`studio-public-page?preview_token=...`), garantindo que o que o usuário vê
 * na prévia é exatamente o que vai ao ar.
 *
 * Regras:
 *  - O corpo é compilado pelo compilador único do Studio (`htmlRenderer.ts`);
 *  - Todo HTML passa pelo saneador do servidor (defesa em profundidade);
 *  - O ÚNICO JavaScript possível é o comportamento oficial do Studio
 *    (`interactive.ts`), incluído apenas quando a página usa um bloco
 *    interativo e liberado na política de segurança por impressão digital.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-11 v1.3.0 - Blocos interativos oficiais: o comportamento
 *            próprio do Studio passa a ser incluído no rodapé da página quando
 *            (e somente quando) algum bloco interativo é usado. Nenhum script
 *            do documento é executado: o texto é fixo e conferido por hash.
 *   RELEASE: 2026-09-11 v1.2.0 - Correção: o formulário do bloco `page.form` era
 *            removido pelo saneador na publicação/prévia. O corpo compilado passa
 *            a ser saneado com `permitirFormulario: true`.
 *   RELEASE: 2026-09-10 v1.1.0 - Correção: o documento público passa a incluir as
 *            folhas de estilo do compilador (`baseStyles()` e `layerStyles()`).
 *            Antes só havia o CSS mínimo da página, então os blocos saíam com as
 *            classes corretas mas SEM desenho (conteúdo empilhado). A moldura da
 *            página deixa de recortar as seções, que são de largura total.
 *   RELEASE: 2026-09-10 v1.0.0 - Versão inicial (Studio, Páginas públicas, Fase 3).
 */

import { sanitizeStudioHtml } from './htmlSanitizer.ts';
// Tipografia governada (conjuntos aprovados e ajustes de título).
import { declaracoesDeTipografia } from './typography.ts';
import { STUDIO_RUNTIME_JS, usaInteracao } from './interactive.ts';
import {
  baseStyles,
  layerStyles,
  renderStudioBody,
  type StudioHtmlDocument,
} from './htmlRenderer.ts';

/** Dados de busca e compartilhamento gravados na publicação. */
export interface StudioPageSeo {
  title?: string | null;
  description?: string | null;
  image_url?: string | null;
  canonical_url?: string | null;
  indexable?: boolean;
}

/** Entrada da montagem do documento. */
export interface StudioPageBuildInput {
  /** Documento em blocos ou `{ html }` quando o formato é HTML importado. */
  document: StudioHtmlDocument | { html?: string };
  /** `blocks` (padrão) ou `raw_html`. */
  sourceFormat?: string;
  seo?: StudioPageSeo;
  /** Título usado quando o SEO não informa um. */
  fallbackTitle?: string;
  /** Endereço da função que recebe o envio do formulário. */
  formAction?: string;
  /** Endereço público da página (usado nos campos ocultos do formulário). */
  pagePath?: string;
  pageHost?: string | null;
  /** Força `noindex` (usado na pré-visualização). */
  forceNoIndex?: boolean;
}

/** Escapa texto para uso em atributos do cabeçalho. */
export function escaparAtributo(valor: unknown): string {
  return String(valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Estilos mínimos de página (aplicados DEPOIS da folha do compilador). */
const CSS_PAGINA = `
  /* O fundo da página vem do tema/ajuste do documento (definido mais abaixo). */
  /* A página NÃO tem moldura: as faixas encostam nas laterais e uma encosta na
     outra, como nas páginas feitas por estúdio. Quem precisa de respiro (vídeo,
     formulário, contato) marca a seção como "com margem lateral" e recebe a
     classe .st-contained, que devolve a largura do miolo. */
  .studio-page { max-width: none; margin: 0; padding: 0; }
  .studio-page > .st-sec { width: 100%; }
  /* Blocos antigos de página continuam centralizados: nunca foram desenhados
     para encostar na borda. */
  .studio-page > *:not(.st-sec) {
    max-width: var(--studio-page-width, 1120px);
    margin-left: auto;
    margin-right: auto;
    padding-left: 24px;
    padding-right: 24px;
  }
  @container doc (max-width: 720px) {
    .page-grid { grid-template-columns: 1fr !important; }
    .st-inner { gap: 24px; }
    .st-lay-split, .st-lay-split-reverse, .st-lay-split-wide-left, .st-lay-bento {
      grid-template-columns: 1fr !important;
    }
    .st-lay-bento > *:first-child { grid-column: auto !important; }
    .st-title-display { font-size: 40px; }
    .st-title-xl { font-size: 34px; }
  }
`;

/**
 * Impressão digital do comportamento oficial, no formato aceito pela política
 * de segurança (`script-src 'sha256-...'`). Só o nosso texto casa com ela.
 */
let hashRuntime: string | null = null;
export async function runtimeCspHash(): Promise<string> {
  if (hashRuntime) return hashRuntime;
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(STUDIO_RUNTIME_JS),
  );
  hashRuntime = `sha256-${btoa(String.fromCharCode(...new Uint8Array(digest)))}`;
  return hashRuntime;
}

/** Impressão digital do artefato (SHA-256 em hexadecimal). */
export async function sha256Hex(texto: string): Promise<string> {
  const dados = new TextEncoder().encode(texto);
  const digest = await crypto.subtle.digest('SHA-256', dados);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Monta o HTML completo da página pública. Lança erro quando fica vazia. */
export function buildStudioPageHtml(entrada: StudioPageBuildInput): string {
  const documento = (entrada.document ?? {}) as StudioHtmlDocument;
  const configuracoes = (documento?.settings ?? {}) as Record<string, unknown>;

  let corpoHtml: string;
  if (String(entrada.sourceFormat ?? 'blocks') === 'raw_html') {
    corpoHtml = sanitizeStudioHtml(String((entrada.document as { html?: string })?.html ?? '')).html;
  } else {
    corpoHtml = renderStudioBody(documento, {
      settings: configuracoes,
      // O compilador lê a definição de campos em `forms` e o destino do envio.
      forms: (configuracoes as { forms?: Record<string, unknown> })?.forms ?? {},
      formAction: entrada.formAction ?? '',
      pagePath: entrada.pagePath ?? '',
      pageHost: entrada.pageHost ?? '',
    });
    // O corpo foi gerado pelo compilador do próprio Studio: o saneamento continua
    // valendo, mas mantém os elementos de formulário do bloco `page.form`.
    corpoHtml = sanitizeStudioHtml(corpoHtml, { permitirFormulario: true }).html;
  }

  if (!corpoHtml.trim()) {
    throw new Error('A página está vazia. Adicione ao menos um bloco.');
  }

  const seo = entrada.seo ?? {};
  const titulo = String(seo.title ?? entrada.fallbackTitle ?? 'Página');
  const descricao = String(seo.description ?? '');
  const imagem = String(seo.image_url ?? '');
  const canonica = String(seo.canonical_url ?? '');
  const indexavel = entrada.forceNoIndex ? false : seo.indexable !== false;
  const paginaCfg = ((configuracoes as { page?: Record<string, unknown> })?.page ?? {}) as {
    maxWidth?: string;
    backgroundColor?: string;
    faviconUrl?: string;
    language?: string;
  };
  const larguraPagina = String(paginaCfg.maxWidth ?? '1120px');

  // ---------------------------------------------------------------------------
  // Aparência geral: o tema do documento (`theme`) e o ajuste de página
  // (`settings.page`) valem também na página publicada, não só na prévia do
  // editor. Sem isto uma página de fundo escuro sai branca ao ser publicada.
  // ---------------------------------------------------------------------------
  const tema = ((documento as { theme?: Record<string, unknown> })?.theme ?? {}) as {
    fontFamily?: string;
    fontSize?: string;
    textColor?: string;
    backgroundColor?: string;
    accentColor?: string;
  };
  /** Só deixa passar valor simples de CSS (sem chaves, ponto e vírgula ou url). */
  const cssSeguro = (valor: unknown, padrao: string): string => {
    const bruto = String(valor ?? '').trim();
    if (!bruto || bruto.length > 120) return padrao;
    if (/[;{}<>()\\]|url|expression|@import/i.test(bruto)) return padrao;
    return bruto;
  };
  const fonteTema = cssSeguro(
    tema.fontFamily,
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
  );
  const tamanhoTema = cssSeguro(tema.fontSize, '16px');
  const corTexto = cssSeguro(tema.textColor, '#111827');
  const corFundo = cssSeguro(paginaCfg.backgroundColor ?? tema.backgroundColor, '#ffffff');
  // Cor de destaque do tema: botões, marcadores e realces da página seguem a
  // marca em vez do azul padrão do Studio.
  const corDestaque = cssSeguro(tema.accentColor, '');
  const idioma = /^[a-zA-Z-]{2,10}$/.test(String(paginaCfg.language ?? ''))
    ? String(paginaCfg.language)
    : 'pt-BR';
  const favicon = /^https:\/\//i.test(String(paginaCfg.faviconUrl ?? ''))
    ? String(paginaCfg.faviconUrl)
    : '';

  return `<!DOCTYPE html>
<html lang="${escaparAtributo(idioma)}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escaparAtributo(titulo)}</title>
${descricao ? `<meta name="description" content="${escaparAtributo(descricao)}" />` : ''}
<meta name="robots" content="${indexavel ? 'index, follow' : 'noindex, nofollow'}" />
${canonica ? `<link rel="canonical" href="${escaparAtributo(canonica)}" />` : ''}
${favicon ? `<link rel="icon" href="${escaparAtributo(favicon)}" />` : ''}
<meta property="og:type" content="website" />
<meta property="og:title" content="${escaparAtributo(titulo)}" />
${descricao ? `<meta property="og:description" content="${escaparAtributo(descricao)}" />` : ''}
${imagem ? `<meta property="og:image" content="${escaparAtributo(imagem)}" />` : ''}
<meta name="twitter:card" content="${imagem ? 'summary_large_image' : 'summary'}" />
<style>
  :root { --studio-page-width: ${escaparAtributo(larguraPagina)};${
    corDestaque ? ` --st-accent: ${corDestaque};` : ''
  } ${declaracoesDeTipografia(tema)} }
  * { box-sizing: border-box; }
  /* Mesmas folhas usadas pelo compilador — é o que faz a página publicada ficar
     idêntica à prévia do editor (que carrega studioLayers.css no navegador). */
  ${baseStyles()}
  ${layerStyles()}
  /* A tipografia da tela vem DEPOIS da folha base (que é voltada à impressão). */
  body { margin: 0; font-family: var(--st-font, ${fonteTema}); font-size: ${tamanhoTema}; color: ${corTexto}; background: ${corFundo}; }
  /* Link herda a cor do bloco: o azul do navegador quebra páginas escuras. */
  .studio-page a { color: inherit; }
  ${CSS_PAGINA}
</style>
</head>

<body>
<!-- studio-doc: raiz que mede a largura para as regras responsivas (v1.7.0). -->
<main class="studio-page studio-doc">
${corpoHtml}
</main>
${usaInteracao(corpoHtml) ? `<script>${STUDIO_RUNTIME_JS}</script>` : ''}
</body>
</html>`;
}

// =============================================================================
// Token de pré-visualização (assinado, curta duração, sem tabela nova)
// =============================================================================

/** Converte bytes em base64url (sem padding). */
function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Assinatura HMAC-SHA256 do conteúdo do token. */
async function assinar(conteudo: string, segredo: string): Promise<string> {
  const chave = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(segredo),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const assinatura = await crypto.subtle.sign('HMAC', chave, new TextEncoder().encode(conteudo));
  return base64Url(new Uint8Array(assinatura));
}

/**
 * Gera um token temporário de pré-visualização.
 * Formato: `<templateId>.<expiraEm>.<assinatura>`.
 */
export async function criarTokenPreview(
  templateId: string,
  segredo: string,
  validadeSegundos = 900,
): Promise<{ token: string; expiresAt: number }> {
  const expiraEm = Math.floor(Date.now() / 1000) + validadeSegundos;
  const conteudo = `${templateId}.${expiraEm}`;
  const assinatura = await assinar(conteudo, segredo);
  return { token: `${conteudo}.${assinatura}`, expiresAt: expiraEm };
}

/** Valida o token e devolve o modelo autorizado, ou `null` quando inválido. */
export async function validarTokenPreview(
  token: string,
  segredo: string,
): Promise<string | null> {
  const partes = String(token ?? '').split('.');
  if (partes.length !== 3) return null;

  const [templateId, expiraTexto, assinatura] = partes;
  const expiraEm = Number(expiraTexto);
  if (!Number.isFinite(expiraEm) || expiraEm < Math.floor(Date.now() / 1000)) return null;

  const esperada = await assinar(`${templateId}.${expiraEm}`, segredo);
  // Comparação de tamanho fixo evita vazar informação por tempo de resposta.
  if (esperada.length !== assinatura.length) return null;
  let diferenca = 0;
  for (let i = 0; i < esperada.length; i++) {
    diferenca |= esperada.charCodeAt(i) ^ assinatura.charCodeAt(i);
  }
  return diferenca === 0 ? templateId : null;
}
